(function giftechGoogleTracking(windowObject, documentObject) {
  "use strict";

  const MAX_ITEMS = 100;
  const context = windowObject.GiftechGoogleTrackingContext || {};
  const dataLayer = (windowObject.dataLayer = windowObject.dataLayer || []);
  const nativeFetch = windowObject.fetch;
  let cartState = normalizeCartItems(context.cart?.items);
  let mutationQueue = Promise.resolve();
  let corporateLeadTracked = false;
  let cartFallbackTimer = 0;
  let pendingAddResponses = 0;
  let lastCartBroadcastAt = 0;
  let cartNavigationStarting = false;

  const cleanCartState =
    windowObject.CleanCartState ||
    (() => {
      const state = {
        cart: context.cart?.items ? context.cart : null,
        controller: null,
        inFlight: null,
        lastUpdatedAt: 0,
        revision: 0,
        sequence: 0,
      };

      const readCart = async ({ signal } = {}) => {
        if (typeof nativeFetch !== "function") return null;

        const response = await nativeFetch.call(windowObject, "/cart.js", {
          credentials: "same-origin",
          signal,
          headers: {
            Accept: "application/json",
            "X-Giftech-Tracking": "true",
          },
        });

        if (!response.ok) throw new Error("Unable to refresh cart.");
        return response.json();
      };

      const publish = (cart) => {
        if (!cart || !Array.isArray(cart.items)) return cart;
        state.revision += 1;
        state.controller?.abort();
        state.cart = cart;
        state.lastUpdatedAt = Date.now();
        return cart;
      };

      const cancel = () => {
        state.sequence += 1;
        state.controller?.abort();
        state.controller = null;
        state.inFlight = null;
      };

      const refresh = (loader = readCart, { supersede = false } = {}) => {
        if (state.inFlight && !supersede) return state.inFlight;
        if (supersede) cancel();

        const controller = new AbortController();
        const sequence = ++state.sequence;
        const revision = state.revision;
        const operation = Promise.resolve()
          .then(() => loader({ signal: controller.signal, sequence }))
          .then((cart) => {
            if (
              controller.signal.aborted ||
              sequence !== state.sequence ||
              revision !== state.revision
            ) {
              const error = new Error("Cart refresh was superseded.");
              error.name = "AbortError";
              throw error;
            }
            if (!cart || !Array.isArray(cart.items)) return cart;
            state.cart = cart;
            state.lastUpdatedAt = Date.now();
            return cart;
          })
          .finally(() => {
            if (state.inFlight === operation) {
              state.controller = null;
              state.inFlight = null;
            }
          });

        state.controller = controller;
        state.inFlight = operation;
        return operation;
      };

      return { cancel, publish, refresh, state };
    })();

  windowObject.CleanCartState = cleanCartState;

  function compact(object) {
    Object.keys(object).forEach((key) => {
      if (
        object[key] === undefined ||
        object[key] === null ||
        object[key] === ""
      ) {
        delete object[key];
      }
    });

    return object;
  }

  function safeText(value, maxLength = 300) {
    if (value === undefined || value === null) return undefined;

    return String(value)
      .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "(redacted)")
      .replace(/(?:\+?\d[\d\s().-]{7,}\d)/g, "(redacted)")
      .replace(
        /\b\d{1,6}\s+[A-Z0-9.'-]+(?:\s+[A-Z0-9.'-]+){0,5}\s+(?:street|st|road|rd|avenue|ave|lane|ln|drive|dr|boulevard|blvd|way|court|ct)\b/gi,
        "(redacted)",
      )
      .replace(/\b\d{5,6}\b/g, "(redacted)")
      .slice(0, maxLength);
  }

  function isSensitiveKey(key) {
    return /^(email|email_address|customer_email|phone|phone_number|mobile|first_name|firstname|last_name|lastname|surname|full_name|address|address1|address2|street|postcode|postal_code|zip)$/i.test(
      key,
    );
  }

  function safeUrl(value) {
    if (!value) return undefined;

    const raw = String(value).slice(0, 2048);

    try {
      const url = new URL(raw, windowObject.location.origin);

      url.searchParams.forEach((parameterValue, parameterKey) => {
        const redactedValue = safeText(parameterValue, 500);

        if (isSensitiveKey(parameterKey) || redactedValue !== parameterValue) {
          url.searchParams.set(parameterKey, "(redacted)");
        }
      });

      return safeText(url.toString(), 2048);
    } catch (error) {
      return safeText(raw, 2048);
    }
  }

  function numeric(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
  }

  function moneyFromCents(value) {
    const amount = numeric(value);
    return amount === undefined ? undefined : amount / 100;
  }

  function pageContext() {
    return compact({
      page_location: safeUrl(windowObject.location.href),
      page_title: safeText(documentObject.title, 300),
      page_referrer: safeUrl(documentObject.referrer),
    });
  }

  function normalizeItem(item, quantityOverride) {
    if (!item) return null;

    const quantity = numeric(quantityOverride ?? item.quantity);
    const price =
      moneyFromCents(item.final_price) ??
      moneyFromCents(item.discounted_price) ??
      moneyFromCents(item.original_price) ??
      numeric(item.price);

    return compact({
      key: safeText(item.key, 150),
      item_id: safeText(item.item_id ?? item.variant_id ?? item.id, 100),
      item_name: safeText(
        item.item_name ?? item.product_title ?? item.title,
        200,
      ),
      item_brand: safeText(item.item_brand ?? item.vendor, 100),
      item_category: safeText(item.item_category ?? item.product_type, 100),
      item_variant: safeText(item.item_variant ?? item.variant_title, 100),
      item_list_id: safeText(item.item_list_id, 100),
      item_list_name: safeText(item.item_list_name, 200),
      price,
      quantity,
      index: numeric(item.index),
    });
  }

  function analyticsItem(item, quantityOverride) {
    const normalized = normalizeItem(item, quantityOverride);
    if (!normalized) return null;

    delete normalized.key;
    return normalized;
  }

  function normalizeCartItems(items) {
    if (!Array.isArray(items)) return [];
    return items
      .slice(0, MAX_ITEMS)
      .map((item) => normalizeItem(item))
      .filter(Boolean);
  }

  function pushEvent(eventName, properties) {
    dataLayer.push(
      Object.assign(
        {
          event: eventName,
        },
        pageContext(),
        properties || {},
      ),
    );
  }

  function pushEcommerce(eventName, ecommerce) {
    dataLayer.push({ ecommerce: null });
    pushEvent(eventName, {
      ecommerce: compact(ecommerce),
    });
  }

  function itemValue(items) {
    return items.reduce((total, item) => {
      const price = numeric(item.price) || 0;
      const quantity = numeric(item.quantity) || 0;
      return total + price * quantity;
    }, 0);
  }

  function pushCartDelta(eventName, items, currency = context.currency) {
    const analyticsItems = items
      .map((item) => analyticsItem(item))
      .filter(Boolean);
    if (!analyticsItems.length) return;

    pushEcommerce(eventName, {
      currency: safeText(currency, 10),
      value: itemValue(analyticsItems),
      items: analyticsItems,
    });
  }

  function aggregateCart(items) {
    const aggregate = new Map();

    normalizeCartItems(items).forEach((item) => {
      const id = String(item.item_id || "");
      if (!id) return;

      const existing = aggregate.get(id);
      if (existing) {
        existing.quantity =
          (numeric(existing.quantity) || 0) + (numeric(item.quantity) || 0);
      } else {
        aggregate.set(id, Object.assign({}, item));
      }
    });

    return aggregate;
  }

  function processCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;

    const previous = aggregateCart(cartState);
    const nextItems = normalizeCartItems(cart.items);
    const next = aggregateCart(nextItems);
    const added = [];
    const removed = [];

    new Set([...previous.keys(), ...next.keys()]).forEach((id) => {
      const previousItem = previous.get(id);
      const nextItem = next.get(id);
      const previousQuantity = numeric(previousItem?.quantity) || 0;
      const nextQuantity = numeric(nextItem?.quantity) || 0;
      const difference = nextQuantity - previousQuantity;

      if (difference > 0 && nextItem) {
        added.push(normalizeItem(nextItem, difference));
      } else if (difference < 0 && previousItem) {
        removed.push(normalizeItem(previousItem, Math.abs(difference)));
      }
    });

    cartState = nextItems;
    pushCartDelta("shopify_product_added_to_cart", added, cart.currency);
    pushCartDelta("shopify_product_removed_from_cart", removed, cart.currency);
  }

  function cartMutationPath(input) {
    const rawUrl = typeof input === "string" ? input : input?.url;
    if (!rawUrl) return null;

    try {
      const url = new URL(rawUrl, windowObject.location.origin);
      const match = url.pathname.match(
        /\/cart\/(add|change|update|clear)(?:\.js)?$/i,
      );
      return match ? match[1].toLowerCase() : null;
    } catch (error) {
      return null;
    }
  }

  async function fetchCartState() {
    const cart = await cleanCartState.refresh();
    if (cart?.items) cartState = normalizeCartItems(cart.items);
    return cart;
  }

  function applyAddedItemsToCartState(addedItems) {
    addedItems.forEach((item) => {
      const existing = cartState.find(
        (cartItem) => cartItem.item_id === item.item_id,
      );

      if (existing) {
        existing.quantity =
          (numeric(existing.quantity) || 0) + (numeric(item.quantity) || 0);
      } else {
        cartState.push(item);
      }
    });
  }

  function scheduleCartFallback(mutationStartedAt) {
    if (cartNavigationStarting || lastCartBroadcastAt >= mutationStartedAt)
      return;
    windowObject.clearTimeout(cartFallbackTimer);
    cartFallbackTimer = windowObject.setTimeout(() => {
      cartFallbackTimer = 0;
      if (cartNavigationStarting) return;
      mutationQueue = mutationQueue
        .then(() => fetchCartState())
        .catch(() => {});
    }, 1200);
  }

  function requestedQuantities(body) {
    const quantities = new Map();

    function add(id, quantity) {
      const key = String(id || "");
      const amount = numeric(quantity) || 1;
      if (!key) return;
      quantities.set(key, (quantities.get(key) || 0) + amount);
    }

    try {
      if (body instanceof FormData || body instanceof URLSearchParams) {
        add(body.get("id"), body.get("quantity"));
      } else if (typeof body === "string") {
        const trimmed = body.trim();

        if (trimmed.startsWith("{")) {
          const payload = JSON.parse(trimmed);
          if (Array.isArray(payload.items)) {
            payload.items.forEach((item) => add(item.id, item.quantity));
          } else {
            add(payload.id, payload.quantity);
          }
        } else {
          const payload = new URLSearchParams(body);
          add(payload.get("id"), payload.get("quantity"));
        }
      }
    } catch (error) {
      return quantities;
    }

    return quantities;
  }

  async function processMutationResponse(
    path,
    responseData,
    body,
    mutationStartedAt,
  ) {
    if (!responseData) return;

    if (path === "add") {
      const quantities = requestedQuantities(body);
      const responseItems = Array.isArray(responseData.items)
        ? responseData.items
        : [responseData];
      const addedItems = responseItems
        .slice(0, MAX_ITEMS)
        .map((item) =>
          normalizeItem(
            item,
            quantities.get(String(item.variant_id ?? item.id)) ?? item.quantity,
          ),
        )
        .filter(Boolean);

      pushCartDelta(
        "shopify_product_added_to_cart",
        addedItems,
        responseData.currency,
      );
      applyAddedItemsToCartState(addedItems);
      scheduleCartFallback(mutationStartedAt);

      return;
    }

    processCart(responseData);
  }

  function queueMutation(
    path,
    getResponseData,
    body,
    mutationStartedAt = Date.now(),
  ) {
    if (path === "add") pendingAddResponses += 1;
    mutationQueue = mutationQueue
      .then(async () => {
        const responseData = await getResponseData();
        await processMutationResponse(
          path,
          responseData,
          body,
          mutationStartedAt,
        );
      })
      .finally(() => {
        if (path === "add")
          pendingAddResponses = Math.max(0, pendingAddResponses - 1);
      })
      .catch(() => {});
  }

  function installFetchTracking() {
    if (typeof nativeFetch !== "function") return;

    windowObject.fetch = function trackedFetch(input, init) {
      const path = cartMutationPath(input);
      const mutationStartedAt = Date.now();
      const responsePromise = nativeFetch.apply(this, arguments);

      if (
        path &&
        !(
          init?.headers &&
          new Headers(init.headers).get("X-Giftech-Tracking") === "true"
        )
      ) {
        responsePromise
          .then((response) => {
            if (!response.ok) return;
            queueMutation(
              path,
              () => response.clone().json(),
              init?.body,
              mutationStartedAt,
            );
          })
          .catch(() => {});
      }

      return responsePromise;
    };
  }

  function installXhrTracking() {
    const xhrPrototype = windowObject.XMLHttpRequest?.prototype;
    if (!xhrPrototype?.open || !xhrPrototype?.send) return;

    const nativeOpen = xhrPrototype.open;
    const nativeSend = xhrPrototype.send;

    xhrPrototype.open = function trackedXhrOpen(method, url) {
      this.giftechCartMutationPath = cartMutationPath(url);
      return nativeOpen.apply(this, arguments);
    };

    xhrPrototype.send = function trackedXhrSend(body) {
      if (this.giftechCartMutationPath) {
        this.giftechCartMutationStartedAt = Date.now();
        this.addEventListener(
          "load",
          () => {
            if (this.status < 200 || this.status >= 300) return;

            queueMutation(
              this.giftechCartMutationPath,
              () => {
                if (this.responseType === "json" && this.response)
                  return this.response;
                return JSON.parse(this.responseText);
              },
              body,
              this.giftechCartMutationStartedAt,
            );
          },
          { once: true },
        );
      }

      return nativeSend.apply(this, arguments);
    };
  }

  function trackCorporateLead() {
    if (corporateLeadTracked) return;

    const pageLocation = String(pageContext().page_location || "");
    if (!/\/pages\/corporate-gifts(?:[/?#]|$)/i.test(pageLocation)) return;
    if (!/[?&]thank-you=true(?:[&#]|$)/i.test(pageLocation)) return;

    corporateLeadTracked = true;
    pushEvent("shopify_corporate_gifts_lead_generated");
  }

  function pushInitialEvents() {
    pushEvent("shopify_page_viewed");

    if (context.product) {
      const item = analyticsItem(context.product);
      if (item) {
        pushEcommerce("shopify_product_viewed", {
          currency: safeText(context.currency, 10),
          value: numeric(item.price),
          items: [item],
        });
      }
    }

    if (context.collection) {
      const items = normalizeCartItems(context.collection.items)
        .map((item) => analyticsItem(item))
        .filter(Boolean);

      pushEcommerce("shopify_collection_viewed", {
        currency: safeText(context.currency, 10),
        item_list_id: safeText(context.collection.item_list_id, 100),
        item_list_name: safeText(context.collection.item_list_name, 200),
        items,
      });
    }

    if (context.pageType === "cart" && context.cart) {
      const items = normalizeCartItems(context.cart.items)
        .map((item) => analyticsItem(item))
        .filter(Boolean);

      pushEcommerce("shopify_cart_viewed", {
        currency: safeText(context.currency, 10),
        value: numeric(context.cart.value),
        items,
      });
    }

    const searchTerm = safeText(context.searchTerm, 100);
    if (context.pageType === "search" && searchTerm) {
      pushEvent("shopify_search_submitted", {
        search_term: searchTerm,
      });
    }

    trackCorporateLead();
  }

  windowObject.GiftechGoogleTracking = {
    trackCorporateLead,
    waitForCartAddTracking: () => mutationQueue,
  };

  documentObject.addEventListener("clean:cart:updated", (event) => {
    if (!event.detail?.cart) return;
    windowObject.clearTimeout(cartFallbackTimer);
    cartFallbackTimer = 0;
    lastCartBroadcastAt = Date.now();
    cleanCartState.publish(event.detail.cart);

    if (pendingAddResponses > 0) {
      return;
    }

    mutationQueue = mutationQueue
      .then(() => processCart(event.detail.cart))
      .catch(() => {});
  });

  documentObject.addEventListener("clean:cart:navigation-starting", () => {
    cartNavigationStarting = true;
    cleanCartState.cancel?.();
    windowObject.clearTimeout(cartFallbackTimer);
    cartFallbackTimer = 0;
  });

  documentObject.addEventListener("clean:cart:navigation-cancelled", () => {
    cartNavigationStarting = false;
  });

  windowObject.addEventListener("pageshow", (event) => {
    if (event.persisted) cartNavigationStarting = false;
  });

  installFetchTracking();
  installXhrTracking();
  pushInitialEvents();
})(window, document);
